import React, { useState } from "react";
import { Offcanvas } from "react-bootstrap";
import { ReleaseNoteItem } from "../../interfaces";
import { renderReleaseMarkdown } from "./releaseMarkdown";

interface WhatsNewDrawerProps {
  show: boolean;
  onHide: () => void;
  notes: ReleaseNoteItem[] | null;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  onRead: (note: ReleaseNoteItem) => void;
}

// "2026-09-18" -> "18 September 2026". Built in UTC so no time zone can shift the day.
export const formatReleaseDate = (isoDate: string) => {
  const [year, month, day] = isoDate.split("-").map(Number);
  if (!year || !month || !day) return isoDate;
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
};

const ReleaseNoteCard: React.FC<{
  note: ReleaseNoteItem;
  onRead: (note: ReleaseNoteItem) => void;
}> = ({ note, onRead }) => {
  const [expanded, setExpanded] = useState(false);
  const bodyId = `release-note-${note.release_note_id}`;

  const toggle = () => {
    if (!expanded) onRead(note);
    setExpanded(!expanded);
  };

  return (
    <li className="tw-py-4 tw-border-0 tw-border-b tw-border-solid tw-border-[#e3e8ee] last:tw-border-b-0">
      <div className="tw-flex tw-items-center tw-gap-2">
        <h3 className="tw-m-0 tw-text-base tw-font-semibold tw-text-[#03234a]">
          {note.title}
        </h3>
        {note.is_new && (
          <span className="tw-rounded-full tw-bg-[#e7f0fb] tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-[#03234a]">
            New
          </span>
        )}
      </div>
      <p className="tw-m-0 tw-mt-0.5 tw-text-xs tw-text-[#5b6b7c]">
        v{note.version} ·{" "}
        <time dateTime={note.release_date}>
          {formatReleaseDate(note.release_date)}
        </time>
      </p>
      {note.summary && (
        <p className="tw-m-0 tw-mt-2 tw-text-sm tw-text-[#26323f]">
          {note.summary}
        </p>
      )}
      <button
        type="button"
        className="tw-mt-2 tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-semibold tw-text-[#0b5cad] hover:tw-underline tw-cursor-pointer"
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={toggle}
      >
        {expanded ? "Hide release notes" : "Read release notes"}
      </button>
      {expanded && (
        <div id={bodyId} className="release-markdown tw-mt-2">
          {renderReleaseMarkdown(note.body_markdown)}
        </div>
      )}
    </li>
  );
};

const WhatsNewDrawer: React.FC<WhatsNewDrawerProps> = ({
  show,
  onHide,
  notes,
  loading,
  failed,
  onRetry,
  onRead,
}) => (
  <Offcanvas
    show={show}
    onHide={onHide}
    placement="end"
    className="whats-new-drawer"
    aria-labelledby="whats-new-title"
  >
    <Offcanvas.Header closeButton closeLabel="Close what's new">
      <Offcanvas.Title id="whats-new-title" as="h2" className="tw-text-lg tw-m-0">
        What's new
      </Offcanvas.Title>
    </Offcanvas.Header>
    <Offcanvas.Body className="tw-pt-0">
      {failed ? (
        <div role="alert" className="tw-text-sm">
          <p className="tw-mb-2">The release notes could not be loaded.</p>
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : loading && !notes ? (
        <p className="tw-text-sm tw-text-[#5b6b7c]">Loading release notes…</p>
      ) : notes && notes.length === 0 ? (
        <p className="tw-text-sm tw-text-[#5b6b7c]">No release notes yet.</p>
      ) : (
        <ul className="tw-list-none tw-m-0 tw-p-0">
          {(notes ?? []).map((note) => (
            <ReleaseNoteCard key={note.release_note_id} note={note} onRead={onRead} />
          ))}
        </ul>
      )}
    </Offcanvas.Body>
  </Offcanvas>
);

export default WhatsNewDrawer;
